import { Portal } from "@gorhom/portal";
import { isWeb } from "@memorang/helpers/platform";
import { getWordCount } from "@memorang/helpers/string";
import type { WordLimit } from "@memorang/types";
import type { TFunction } from "i18next";
import { use, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type TextInput as RNTextInput, View } from "react-native";
import { Text, TextInput } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import CustomBottomSheetTextInput from "../../CustomBottomSheetTextInput";
import { WritingSheetPortalContext } from "../WritingSheetPortalContext";
import { type Selection, WritingToolbar } from "./WritingToolbar";
import { WordCountChip } from "./WritingToolbar/WordCountChip";

const MAX_LENGTH = 6000;
const MIN_HEIGHT = 200;
const MAX_HEIGHT = 400;

type Props = {
	value?: string;
	onChangeText: (value: string) => void;
	disabled?: boolean;
	isAnswered?: boolean;
	placeholder?: string;
	wordCountText?: string;
	wordLimit?: WordLimit;
	showHelperText?: boolean;
	showWordLimitError?: boolean;
	autoExpand?: boolean;
};

const getChipText = ({
	wordCount,
	wordLimit,
	wordCountText,
	pluralWord,
}: {
	wordCount: number;
	wordLimit?: WordLimit;
	wordCountText?: string;
	pluralWord: string;
}) => {
	if (wordCountText) {
		return wordCountText;
	}
	if (wordLimit?.max != null) {
		return `${wordCount} / ${wordLimit.max} ${pluralWord}`;
	}
	return `${wordCount} ${pluralWord}`;
};

const getHelperText = ({
	wordLimit,
	wordCount,
	t,
}: {
	wordLimit?: WordLimit;
	wordCount: number;
	t: TFunction;
}) => {
	if (wordLimit?.min != null && wordCount < wordLimit.min) {
		return wordLimit.max != null
			? t("writing.helper.range", { min: wordLimit.min, max: wordLimit.max })
			: t("writing.helper.min", { min: wordLimit.min });
	}

	if (wordLimit?.max != null && wordCount > wordLimit.max) {
		return t("writing.helper.max", { max: wordLimit.max });
	}

	return undefined;
};

const WritingInputFooter = ({
	isMobile,
	wordCount,
	wordLimit,
	pluralWord,
	wordCountText,
	helperText,
	isAboveMax,
}: {
	isMobile: boolean;
	wordCount: number;
	wordLimit?: WordLimit;
	pluralWord: string;
	wordCountText?: string;
	helperText?: string;
	isAboveMax: boolean;
}) => {
	const theme = useAppTheme();
	const textColor = isAboveMax
		? theme.colors.error
		: theme.palette.text.secondary;

	return (
		<>
			{isMobile ? (
				<View style={{ alignSelf: "flex-start", marginTop: 1 }}>
					<WordCountChip
						wordCount={wordCount}
						wordLimit={wordLimit}
						pluralWord={pluralWord}
						overrideText={wordCountText}
					/>
				</View>
			) : null}
			{helperText ? (
				<Text variant="bodySmall" style={{ color: textColor }}>
					{helperText}
				</Text>
			) : null}
		</>
	);
};

const WritingInput = ({
	value,
	onChangeText,
	disabled,
	isAnswered,
	placeholder,
	wordCountText,
	wordLimit,
	showHelperText = true,
	showWordLimitError = true,
	autoExpand = false,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const portalHostName = use(WritingSheetPortalContext);
	const [inputHeight, setInputHeight] = useState(MIN_HEIGHT);
	const [selection, setSelection] = useState<Selection | undefined>();
	const containerRef = useRef<View>(null);
	const inputRef = useRef<RNTextInput>(null);

	const wordCount = getWordCount(value);
	const resolvedPlaceholder = placeholder ?? t("writing.placeholder");
	const isAboveMax =
		showWordLimitError && wordLimit?.max != null && wordCount > wordLimit.max;
	const toolbarWordLimit = showWordLimitError
		? wordLimit
		: wordLimit
			? { ...wordLimit, max: undefined }
			: undefined;
	const pluralWord = t("common.word", { count: wordCount });
	const chipText = getChipText({
		wordCount,
		wordLimit,
		wordCountText,
		pluralWord,
	});
	const helperText = showHelperText
		? getHelperText({ wordLimit, wordCount, t })
		: undefined;

	// biome-ignore lint/correctness/useExhaustiveDependencies: value triggers re-measurement of textarea height
	useEffect(() => {
		if (!(isWeb && containerRef.current)) {
			return;
		}
		const el = containerRef.current as unknown as HTMLElement;
		const textarea = el.querySelector("textarea");
		if (!textarea) {
			return;
		}

		textarea.style.height = `${MIN_HEIGHT}px`;
		const contentHeight = Math.max(textarea.scrollHeight, MIN_HEIGHT);
		const newHeight = autoExpand
			? contentHeight
			: Math.min(contentHeight, MAX_HEIGHT);
		textarea.style.height = `${newHeight}px`;
		setInputHeight(newHeight);
	}, [value, autoExpand]);

	useEffect(() => {
		if (selection) {
			inputRef.current?.focus();
		}
	}, [selection]);

	if (isWeb) {
		return (
			<View style={{ gap: 4 }}>
				<View
					ref={containerRef}
					style={{
						borderWidth: 1,
						borderColor: isAboveMax ? theme.colors.error : theme.colors.outline,
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surface,
						overflow: "hidden",
					}}
				>
					<WritingToolbar
						value={value}
						onChangeText={onChangeText}
						onSelectionChange={setSelection}
						selection={selection}
						maxLength={MAX_LENGTH}
						disabled={disabled}
						wordCount={wordCount}
						pluralWord={pluralWord}
						wordLimit={toolbarWordLimit}
						wordCountText={wordCountText}
						hideChip={isMobile}
						withBottomBorder
					/>
					<TextInput
						ref={inputRef}
						placeholder={resolvedPlaceholder}
						disabled={disabled}
						error={isAboveMax}
						onChangeText={onChangeText}
						selection={selection}
						onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
						contentStyle={{
							minWidth: 300,
							height: inputHeight,
							maxHeight: autoExpand ? undefined : MAX_HEIGHT,
							overflow: autoExpand ? "hidden" : "scroll",
							paddingHorizontal: 16,
							paddingVertical: 8,
						}}
						style={{ backgroundColor: theme.colors.surface }}
						placeholderTextColor={theme.colors.onSurfaceDisabled}
						multiline
						mode="flat"
						underlineColor="transparent"
						activeUnderlineColor="transparent"
						value={value}
						autoComplete="off"
						autoCorrect={false}
						spellCheck={false}
						autoCapitalize="none"
						maxLength={MAX_LENGTH}
					/>
				</View>
				<WritingInputFooter
					isMobile={isMobile}
					wordCount={wordCount}
					wordLimit={toolbarWordLimit}
					pluralWord={pluralWord}
					wordCountText={wordCountText}
					helperText={helperText}
					isAboveMax={isAboveMax}
				/>
			</View>
		);
	}

	if (isAnswered) {
		return null;
	}

	const sheetProps = {
		value,
		onChangeText,
		placeholder: resolvedPlaceholder,
		wordCount,
		wordCountText: chipText,
		wordLimit: toolbarWordLimit,
		maxLength: MAX_LENGTH,
		disabled,
	};

	return portalHostName ? (
		<Portal hostName={portalHostName}>
			<CustomBottomSheetTextInput {...sheetProps} />
		</Portal>
	) : (
		<CustomBottomSheetTextInput {...sheetProps} />
	);
};

export default WritingInput;
